import {
  SessionServiceInterface,
  SessionServiceStateType,
  OnSessionServiceStateChangedCallback,
} from '@central-products/interface-session-service';
import { StateType as SessionManagerStateType } from '@central-products/interface-session-manager';
import { SessionServiceConfig, SessionServiceDependencies } from './types';
import { HTTPError } from './libs';

export class SessionService extends SessionServiceInterface {
  private readonly config: SessionServiceConfig;
  private readonly dependencies: SessionServiceDependencies;
  private state: SessionServiceStateType = SessionServiceStateType.SESSION_UNKNOWN;
  private onStateChangedCallback?: OnSessionServiceStateChangedCallback;
  private abortController?: AbortController;

  constructor(config: SessionServiceConfig, dependencies: SessionServiceDependencies) {
    super();

    this.config = config;
    this.dependencies = dependencies;
  }

  public async init() {
    const sessionManagerState = await this.dependencies['session-manager'].getState();
    this.dependencies['session-manager'].onStateChanged(this.processSessionManagerState);
    this.processSessionManagerState(sessionManagerState);
  }

  public async getState() {
    return { state: this.state };
  }

  public onStateChanged(callback: OnSessionServiceStateChangedCallback) {
    this.onStateChangedCallback = callback;
  }

  private processSessionManagerState = ({ state }: { state: SessionManagerStateType }) => {
    if (state === SessionManagerStateType.LOGGED_IN) {
      this.logIn();
    }

    if (state === SessionManagerStateType.NOT_LOGGED_IN) {
      this.logOut();
    }
  };

  private async logIn() {
    if (this.state !== SessionServiceStateType.SESSION_UNKNOWN && this.state !== SessionServiceStateType.NO_SESSION) {
      return;
    }

    try {
      this.setState(SessionServiceStateType.SESSION_REQUESTED);
      await this.initSession();
      this.setState(SessionServiceStateType.SESSION_EXISTS);
    } catch (error) {
      this.setState(SessionServiceStateType.NO_SESSION);
    }
  }

  private async logOut() {
    if (this.state === SessionServiceStateType.NO_SESSION) {
      return;
    }

    if (this.state === SessionServiceStateType.SESSION_UNKNOWN) {
      this.setState(SessionServiceStateType.NO_SESSION);
      return;
    }

    if (this.state === SessionServiceStateType.SESSION_REQUESTED) {
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = undefined;
      }
    }

    const previousState = this.state;

    try {
      await this.destroySession();
      this.setState(SessionServiceStateType.NO_SESSION);
    } catch (error) {
      this.setState(previousState);
    }
  }

  private async initSession() {
    const { sessionToken } = await this.dependencies['session-manager'].getSessionToken({
      serviceUrl: `${this.config.sessionServiceUrl}/handle`,
    });

    this.abortController = new AbortController();

    const response = await fetch(`${this.config.sessionServiceUrl}/handle?ticket=${sessionToken}`, {
      credentials: 'include',
      signal: this.abortController.signal,
    });

    if (!response.ok) {
      throw new HTTPError(response);
    }

    return true;
  }

  private async destroySession() {
    const response = await fetch(`${this.config.sessionServiceUrl}/logout`, { credentials: 'include' });

    // TODO: change to `!response.ok` after fixing response behaviour on backend
    if (response.status > 400) {
      throw new HTTPError(response);
    }

    return true;
  }

  private setState = (state: SessionServiceStateType) => {
    if (this.state === state) {
      return;
    }

    this.state = state;

    if (this.onStateChangedCallback) {
      this.onStateChangedCallback({ state });
    }
  };
}
